import { Plus, Trophy } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { FormEvent } from "react";

import { Button } from "@/shared/ui/button";
import { PageHeader } from "@/shared/ui/page-header";
import { GameEditorCard } from "./GameEditorCard";
import { PlayoffSeriesList } from "./PlayoffSeriesList";
import { ScheduleDeleteDialog } from "./ScheduleDeleteDialog";
import { ScheduleList } from "./ScheduleList";
import { ScheduleSeasonFilter } from "./ScheduleSeasonFilter";
import {
  SeriesEditorCard,
  SeriesMetadataEditorCard,
} from "./SeriesEditorCards";
import {
  currentScheduleDate,
  emptyGameEditor,
  emptySeriesEditor,
  gameEditorFromEntry,
  scheduleInputFromEditor,
  schedulePatchFromEditor,
  seriesInputFromEditor,
} from "./scheduleEditor";
import type {
  GameEditorValue,
  SeriesEditorValue,
  SeriesMetadataEditorValue,
} from "./scheduleEditor";
import { compareScheduleEntries } from "./schedulePresentation";
import type { ScheduleEntry, ScheduleScreenProps } from "./scheduleTypes";
import { actionKeys } from "../../hooks/actionKeys";
import { EMPTY_INTENT_STATUS } from "../../hooks/useIntentStatus";

const unavailableMutation = async () => false;
const noop = () => undefined;

export function ScheduleScreen({
  teamName = "Our Team",
  entries,
  series = [],
  seasons = [],
  activeSeasonId,
  players,
  serverBusy,
  actionStatus = EMPTY_INTENT_STATUS,
  onCreate,
  onUpdate,
  onSelect,
  onDelete,
  onCreateSeries = unavailableMutation,
  onUpdateSeries = unavailableMutation,
  onDeleteSeries = unavailableMutation,
  onOpenActive,
  onOpenResult,
  weatherByEntryId = {},
  onRetryWeather = noop,
  weatherRefreshing = false,
}: ScheduleScreenProps) {
  const seasonOptions = useMemo(() => {
    if (seasons.length) return seasons;
    const ids = [
      ...new Set(
        entries.map(
          (entry) => entry.seasonId ?? entry.scheduledDate.slice(0, 4),
        ),
      ),
    ];
    if (!ids.length) ids.push(String(new Date().getUTCFullYear()));
    return ids.map((id) => ({
      id,
      year: /^\d{4}$/.test(id) ? Number(id) : null,
      label: id,
      status: "active" as const,
      revision: 0,
    }));
  }, [entries, seasons]);
  const effectiveActiveSeasonId =
    activeSeasonId === undefined
      ? (seasonOptions[0]?.id ?? null)
      : activeSeasonId;
  const [selectedSeasonId, setSelectedSeasonId] = useState(
    effectiveActiveSeasonId ?? seasonOptions[0]?.id ?? "legacy",
  );
  const previousActiveSeasonId = useRef(effectiveActiveSeasonId);
  useEffect(() => {
    const activeSeasonChanged =
      previousActiveSeasonId.current !== effectiveActiveSeasonId;
    previousActiveSeasonId.current = effectiveActiveSeasonId;
    if (activeSeasonChanged && effectiveActiveSeasonId) {
      setSelectedSeasonId(effectiveActiveSeasonId);
      return;
    }
    if (seasonOptions.some((season) => season.id === selectedSeasonId)) return;
    setSelectedSeasonId(
      effectiveActiveSeasonId ?? seasonOptions[0]?.id ?? "legacy",
    );
  }, [effectiveActiveSeasonId, seasonOptions, selectedSeasonId]);

  const viewingActive = selectedSeasonId === effectiveActiveSeasonId;
  const activeSeasonYear = seasonOptions.find(
    (season) => season.id === effectiveActiveSeasonId,
  )?.year;
  const newScheduleDate = activeSeasonYear
    ? [currentScheduleDate(), `${activeSeasonYear}-01-01`].sort().at(-1)
    : currentScheduleDate();
  const seasonEntries = useMemo(
    () =>
      entries.filter(
        (entry) =>
          (entry.seasonId ?? entry.scheduledDate.slice(0, 4)) ===
          selectedSeasonId,
      ),
    [entries, selectedSeasonId],
  );
  const seasonSeries = useMemo(
    () =>
      series.filter((playoffSeries) => {
        const firstEntry = entries.find(
          (entry) => entry.id === playoffSeries.scheduleIds[0],
        );
        return (
          (playoffSeries.seasonId ??
            firstEntry?.seasonId ??
            firstEntry?.scheduledDate.slice(0, 4)) === selectedSeasonId
        );
      }),
    [entries, selectedSeasonId, series],
  );

  const [editor, setEditor] = useState<GameEditorValue | null>(null);
  const [seriesEditor, setSeriesEditor] = useState<SeriesEditorValue | null>(
    null,
  );
  const [metadataEditor, setMetadataEditor] =
    useState<SeriesMetadataEditorValue | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteSeriesId, setDeleteSeriesId] = useState<string | null>(null);
  const editingEntry = editingId
    ? (seasonEntries.find((entry) => entry.id === editingId) ?? null)
    : null;
  const editingActive = editingEntry?.status === "active";
  const editingSeriesGame = Boolean(editingEntry?.playoffSeriesId);
  const activePlayers = players.filter((player) => player.active);
  const regularEntries = useMemo(
    () => seasonEntries.filter((entry) => !entry.playoffSeriesId),
    [seasonEntries],
  );
  const upcoming = useMemo(
    () =>
      regularEntries
        .filter((entry) => entry.status !== "completed")
        .sort(compareScheduleEntries),
    [regularEntries],
  );
  const results = useMemo(
    () =>
      regularEntries
        .filter((entry) => entry.status === "completed")
        .sort(compareScheduleEntries)
        .reverse(),
    [regularEntries],
  );
  const sortedSeries = useMemo(
    () =>
      [...seasonSeries].sort((left, right) => {
        const leftDate =
          seasonEntries.find((entry) => entry.id === left.scheduleIds[0])
            ?.scheduledDate ?? "";
        const rightDate =
          seasonEntries.find((entry) => entry.id === right.scheduleIds[0])
            ?.scheduledDate ?? "";
        return (
          leftDate.localeCompare(rightDate) ||
          left.title.localeCompare(right.title)
        );
      }),
    [seasonEntries, seasonSeries],
  );
  const gameFormPending = editingId
    ? actionStatus.isPending(actionKeys.scheduleUpdate(editingId))
    : actionStatus.isPending(actionKeys.scheduleCreate);
  const gameFormUnknown =
    actionStatus.phaseFor(
      editingId
        ? actionKeys.scheduleUpdate(editingId)
        : actionKeys.scheduleCreate,
    ) === "outcome_unknown";
  const seriesFormPending = actionStatus.isPending(actionKeys.seriesCreate);
  const seriesFormUnknown =
    actionStatus.phaseFor(actionKeys.seriesCreate) === "outcome_unknown";
  const metadataFormPending = metadataEditor
    ? actionStatus.isPending(actionKeys.seriesUpdate(metadataEditor.id))
    : false;
  const metadataFormUnknown = metadataEditor
    ? actionStatus.phaseFor(actionKeys.seriesUpdate(metadataEditor.id)) ===
      "outcome_unknown"
    : false;

  const closeEditor = () => {
    setEditor(null);
    setEditingId(null);
  };
  const openEditor = (entry: ScheduleEntry) => {
    setSeriesEditor(null);
    setEditingId(entry.id);
    setEditor(gameEditorFromEntry(entry));
  };
  const submitEditor = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editor || !editor.opponentName.trim()) return;
    const succeeded = editingId
      ? await onUpdate(
          editingId,
          schedulePatchFromEditor(editor, activePlayers, {
            active: editingActive,
            seriesGame: editingSeriesGame,
          }),
        )
      : await onCreate(scheduleInputFromEditor(editor, activePlayers));
    if (succeeded) closeEditor();
  };
  const submitSeries = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!seriesEditor?.opponentName.trim() || !seriesEditor.title.trim())
      return;
    const succeeded = await onCreateSeries(
      seriesInputFromEditor(seriesEditor, activePlayers),
    );
    if (succeeded) setSeriesEditor(null);
  };
  const submitSeriesMetadata = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!metadataEditor?.title.trim() || !metadataEditor.opponentName.trim()) {
      return;
    }
    const succeeded = await onUpdateSeries(metadataEditor.id, {
      title: metadataEditor.title.trim(),
      opponentName: metadataEditor.opponentName.trim(),
    });
    if (succeeded) setMetadataEditor(null);
  };

  return (
    <div className="mx-auto w-full max-w-3xl space-y-4">
      <PageHeader
        title="SCHEDULE"
        eyebrow="Games"
        description="Plan upcoming games and open completed box scores."
        actions={
          viewingActive ? (
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  closeEditor();
                  setSeriesEditor(emptySeriesEditor(newScheduleDate));
                }}
              >
                <Trophy aria-hidden="true" />
                ADD SERIES
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  setSeriesEditor(null);
                  setEditingId(null);
                  setEditor(emptyGameEditor(newScheduleDate));
                }}
              >
                <Plus aria-hidden="true" />
                ADD GAME
              </Button>
            </div>
          ) : null
        }
      />
      <ScheduleSeasonFilter
        seasons={seasonOptions}
        activeSeasonId={effectiveActiveSeasonId}
        selectedSeasonId={selectedSeasonId}
        onChange={(seasonId) => {
          closeEditor();
          setSeriesEditor(null);
          setMetadataEditor(null);
          setSelectedSeasonId(seasonId);
        }}
      />
      {seriesEditor ? (
        <SeriesEditorCard
          value={seriesEditor}
          players={activePlayers}
          serverBusy={serverBusy}
          pending={seriesFormPending}
          unknown={seriesFormUnknown}
          onChange={setSeriesEditor}
          onCancel={() => setSeriesEditor(null)}
          onSubmit={submitSeries}
        />
      ) : null}
      {metadataEditor ? (
        <SeriesMetadataEditorCard
          value={metadataEditor}
          serverBusy={serverBusy}
          pending={metadataFormPending}
          unknown={metadataFormUnknown}
          onChange={setMetadataEditor}
          onCancel={() => setMetadataEditor(null)}
          onSubmit={submitSeriesMetadata}
        />
      ) : null}
      {editor ? (
        <GameEditorCard
          value={editor}
          players={activePlayers}
          editing={Boolean(editingId)}
          editingActive={editingActive}
          editingSeriesGame={editingSeriesGame}
          serverBusy={serverBusy}
          pending={gameFormPending}
          unknown={gameFormUnknown}
          onChange={setEditor}
          onCancel={closeEditor}
          onSubmit={submitEditor}
        />
      ) : null}
      <PlayoffSeriesList
        teamName={teamName}
        series={sortedSeries}
        entries={seasonEntries}
        readOnly={!viewingActive}
        onOpen={(entry, availability) => {
          if (entry.status === "active") onOpenActive();
          else if (entry.status === "completed" && entry.gameId) {
            onOpenResult(entry.gameId);
          } else if (viewingActive && availability === "required") {
            void onSelect(entry.id);
          }
        }}
        onEditGame={openEditor}
        onEditSeries={(playoffSeries) => {
          closeEditor();
          setSeriesEditor(null);
          setMetadataEditor({
            id: playoffSeries.id,
            title: playoffSeries.title,
            opponentName: playoffSeries.opponentName,
          });
        }}
        onDeleteSeries={(playoffSeries) => setDeleteSeriesId(playoffSeries.id)}
        weatherByEntryId={weatherByEntryId}
        onRetryWeather={onRetryWeather}
        weatherRefreshing={weatherRefreshing}
        actionStatus={actionStatus}
      />
      <ScheduleList
        title="UPCOMING"
        entries={upcoming}
        empty="No upcoming games. Add one to start building the regular-season schedule."
        readOnly={!viewingActive}
        onOpen={(entry) => {
          if (entry.status === "active") onOpenActive();
          else if (viewingActive) void onSelect(entry.id);
        }}
        {...(viewingActive
          ? {
              onEdit: openEditor,
              onDelete: (entry: ScheduleEntry) => setDeleteId(entry.id),
            }
          : {})}
        weatherByEntryId={weatherByEntryId}
        onRetryWeather={onRetryWeather}
        weatherRefreshing={weatherRefreshing}
        showWeatherAttribution
        actionStatus={actionStatus}
      />
      <ScheduleList
        title="RESULTS"
        entries={results}
        empty="No completed regular-season games yet."
        onOpen={(entry) => {
          if (entry.gameId) onOpenResult(entry.gameId);
        }}
      />
      {deleteId ? (
        <ScheduleDeleteDialog
          title="Delete this scheduled game?"
          description="This only removes the future fixture. It cannot delete a played result."
          serverBusy={serverBusy}
          pending={actionStatus.isPending(actionKeys.scheduleDelete(deleteId))}
          unknown={
            actionStatus.phaseFor(actionKeys.scheduleDelete(deleteId)) ===
            "outcome_unknown"
          }
          onRetryUnknown={actionStatus.retryUnknown}
          onCancel={() => setDeleteId(null)}
          onDelete={async () => {
            const succeeded = await onDelete(deleteId);
            if (succeeded) setDeleteId(null);
          }}
        />
      ) : null}
      {deleteSeriesId ? (
        <ScheduleDeleteDialog
          title="Delete this playoff series?"
          description="All unplayed game slots in this series will be removed together."
          serverBusy={serverBusy}
          pending={actionStatus.isPending(
            actionKeys.seriesDelete(deleteSeriesId),
          )}
          unknown={
            actionStatus.phaseFor(actionKeys.seriesDelete(deleteSeriesId)) ===
            "outcome_unknown"
          }
          onRetryUnknown={actionStatus.retryUnknown}
          onCancel={() => setDeleteSeriesId(null)}
          onDelete={async () => {
            const succeeded = await onDeleteSeries(deleteSeriesId);
            if (succeeded) setDeleteSeriesId(null);
          }}
        />
      ) : null}
    </div>
  );
}
